// Quality Signals — bank health API client (writer agent + platform update-on-ingest).
// Backend: app/routes/signals_bank.py under /api/admin/signals/bank.

import api from './api';

const BASE = '/admin/signals/bank';

export interface BankFlags {
  agent_writer: boolean;
  dedup_event_sync: boolean;
  agent_writer_model: string;
}

export interface EventsSummary {
  total: number;
  by_action: Record<string, number>;
  by_execution_status: Record<string, number>;
  by_consume_state: Record<string, number>;
  last_event_at: string | null;
  last_synced_at: string | null;
  citations_rewritten: number;
  ingestion_rows_repointed: number;
}

export interface WriterSummary {
  window_days: number;
  runs: number;
  by_mode: Record<string, number>;
  by_decision: Record<string, number>;
  fallback_reasons: Record<string, number>;
  with_warnings: number;
  cost_usd: number;
  no_change_runs: number;
  no_change_cost_usd: number;
  double_coverage_runs: number;
  double_coverage_cost_usd: number;
  double_coverage_pipeline_tokens: number;
  by_matched_writer: Record<string, number>;
}

export interface BankHealth {
  allow_updates: boolean | null;
  allow_updates_error: string | null;
  flags: BankFlags;
  events: EventsSummary;
  writer: WriterSummary;
}

export interface DedupEventRow {
  event_id: string;
  action: string;
  source: string | null;
  execution_status: string | null;
  execution_error: string | null;
  target_entry_id: string | null;
  result_entry_id: string | null;
  deleted_entry_ids: string[];
  max_similarity_score: number | null;
  event_created_at: string | null;
  consume_state: string;
  citations_rewritten: number;
  ingestion_rows_repointed: number;
  consume_error: string | null;
  reasoning: string | null;
  content_preview: string | null;
}

export interface SyncResult {
  listed: number;
  recorded: number;
  applied: number;
  no_action: number;
  failed: number;
  citations_rewritten: number;
  ingestion_rows_repointed: number;
  action_counts: Record<string, number>;
  allow_updates: boolean | null;
  error: string | null;
}

export interface WriterRunRow {
  matched_writer: string | null;
  double_coverage: boolean;
  record_id: string;
  mailbox: string;
  email_id: string;
  email_subject: string | null;
  state: string;
  mode: string | null;
  decision: string | null;
  kb_entry_id: string | null;
  matched_entry_id: string | null;
  reason: string | null;
  fallback_reason: string | null;
  conversation_id: string | null;
  cost_usd: number | null;
  validation_warnings: string[];
  processed_at: string | null;
}

export const signalsBankApi = {
  health: async (days = 14): Promise<BankHealth> => {
    const r = await api.get<BankHealth>(`${BASE}/health`, { params: { days } });
    return r.data;
  },
  events: async (limit = 50, action?: string): Promise<{ items: DedupEventRow[]; total: number }> => {
    const r = await api.get<{ items: DedupEventRow[]; total: number }>(`${BASE}/events`, {
      params: { limit, ...(action ? { action } : {}) },
    });
    return r.data;
  },
  sync: async (): Promise<SyncResult> => {
    const r = await api.post<SyncResult>(`${BASE}/events/sync`);
    return r.data;
  },
  writerRuns: async (days = 14, limit = 50): Promise<{ items: WriterRunRow[]; total: number }> => {
    const r = await api.get<{ items: WriterRunRow[]; total: number }>(`${BASE}/writer-runs`, {
      params: { days, limit },
    });
    return r.data;
  },
};
